import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api from '@/lib/api';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';
import AnalyticsDrillDialog from '@/components/AnalyticsDrillDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ChartLine, Buildings, UserCircle, Target, Package, Money, Users, TrendUp,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';

const COLORS = ['#0F4C81', '#0F4C81', '#40916C', '#52B788', '#74C69D', '#95D5B2', '#B7E4C7', '#D8F3DC'];
const STATUS_COLORS = { paid: '#40916C', partially_paid: '#E9C46A', overdue: '#DC2626', sent: '#457B9D', draft: '#999', credit_noted: '#DC2626' };
const fmt = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
const fmtK = (v) => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : `₹${v}`;

const TABS = [
  { key: 'revenue', label: 'Revenue', icon: ChartLine },
  { key: 'clinics', label: 'Clinics', icon: Buildings },
  { key: 'audiologists', label: 'Audiologists', icon: UserCircle },
  { key: 'lead_sources', label: 'Lead Sources', icon: Target },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'collections', label: 'Collections', icon: Money },
  { key: 'customers', label: 'Customers', icon: Users },
];

export default function AnalyticsPage() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? `${now.getFullYear()}-04-01` : `${now.getFullYear() - 1}-04-01`;
  const fyEnd = now.getMonth() >= 3 ? `${now.getFullYear() + 1}-03-31` : `${now.getFullYear()}-03-31`;

  const [tab, setTab] = useState('revenue');
  const [startDate, setStartDate] = useState(fyStart);
  const [endDate, setEndDate] = useState(fyEnd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillSpec, setDrillSpec] = useState(null);

  const openDrill = (dimension, value, label) => {
    if (value === undefined || value === null || value === '') return;
    setDrillSpec({ dimension, value: String(value), label: label || String(value) });
  };

  const switchTab = (t) => { setData(null); setTab(t); };

  const loadData = async (section) => {
    setLoading(true);
    setData(null);
    try {
      const endpoints = {
        revenue: '/analytics/revenue',
        clinics: '/analytics/clinic-performance',
        audiologists: '/analytics/audiologist-performance',
        lead_sources: '/analytics/lead-sources',
        products: '/analytics/products',
        collections: '/analytics/collections',
        customers: '/analytics/customers',
      };
      const { data: result } = await api.get(endpoints[section], { params: { start_date: startDate, end_date: endDate } });
      setData(result);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(tab); }, [tab, startDate, endDate]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-3 text-xs">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' && p.value > 100 ? fmt(p.value) : p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6" data-testid="analytics-page">
      <PageHeader title="Analytics & Insights" subtitle="Business intelligence, trends, and performance metrics" />

      {/* Date Filter */}
      <div className="flex items-end gap-3 mt-4 mb-4">
        <div>
          <label className="text-[10px] text-[#64748B] block mb-1">FROM</label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs w-36" data-testid="analytics-from" />
        </div>
        <div>
          <label className="text-[10px] text-[#64748B] block mb-1">TO</label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs w-36" data-testid="analytics-to" />
        </div>
        <Button size="sm" variant="outline" onClick={() => { setStartDate(fyStart); setEndDate(fyEnd); }} className="h-8 text-xs">Current FY</Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E2E8F0] mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => switchTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {loading || !data ? (
        <div className="py-16 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {/* ─── Revenue Tab ─── */}
          {tab === 'revenue' && (
            <>
              {/* KPI Cards — Total & Net Revenue computed exactly like the Dashboard */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Revenue', value: fmt(data.totals?.total_revenue), sub: `${data.totals?.total_invoices || 0} invoices · excl. inter-branch`, color: 'bg-[#EBF3FA] text-[#0F4C81]', testId: 'analytics-total-revenue' },
                  { label: 'Net Revenue', value: fmt(data.totals?.net_revenue), sub: '+ Sales Orders − Credit Notes', color: 'bg-[#0F4C81] text-white', testId: 'analytics-net-revenue' },
                  { label: 'GST Collected', value: fmt(data.totals?.total_tax), color: 'bg-blue-50 text-blue-800' },
                  { label: 'Outstanding', value: fmt(data.totals?.total_outstanding), color: 'bg-red-50 text-[#DC2626]' },
                ].map((kpi, i) => (
                  <div key={i} className={`rounded-xl p-4 ${kpi.color}`} data-testid={kpi.testId}>
                    <p className="text-[10px] uppercase tracking-wide opacity-70">{kpi.label}</p>
                    <p className="text-xl font-bold mt-1">{kpi.value}</p>
                    {kpi.sub && <p className="text-[10px] mt-0.5 opacity-60">{kpi.sub}</p>}
                  </div>
                ))}
              </div>

              {/* Revenue Breakdown — mirrors the Dashboard (Total + SO − CN = Net) */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="analytics-revenue-breakdown">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Invoice Revenue</p>
                    <p className="text-lg font-bold text-[#0F4C81]">{fmt(data.totals?.total_revenue)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">+ Sales Orders</p>
                    <p className="text-lg font-bold text-emerald-700" data-testid="analytics-so-total">{fmt(data.totals?.sales_orders_total)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">− Credit Notes</p>
                    <p className="text-lg font-bold text-red-600" data-testid="analytics-cn-total">{fmt(data.totals?.credit_notes_total)}</p>
                  </div>
                  <div className="bg-[#0F4C81] rounded-lg px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-emerald-300 mb-1">= Net Revenue</p>
                    <p className="text-lg font-bold text-white" data-testid="analytics-breakdown-net">{fmt(data.totals?.net_revenue)}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between" data-testid="analytics-inter-branch-line">
                  <span className="text-[11px] text-[#64748B]">Inter-branch Transfers <span className="text-[#94A3B8]">(internal — excluded from revenue)</span> · {data.totals?.inter_branch_count || 0} entries</span>
                  <span className="text-sm font-semibold text-[#94A3B8]" data-testid="analytics-inter-branch-total">{fmt(data.totals?.inter_branch_transfers)}</span>
                </div>
              </div>

              {/* Revenue Trend Chart */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Monthly Revenue Trend</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data.monthly} onClick={(s) => s?.activeLabel && openDrill('month', s.activeLabel, 'Month: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0F4C81" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#0F4C81" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="revenue" stroke="#0F4C81" fill="url(#revGrad)" strokeWidth={2} name="Revenue" />
                    <Line type="monotone" dataKey="tax" stroke="#457B9D" strokeWidth={1.5} strokeDasharray="4 4" name="GST" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Revenue by Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue by Payment Status</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data.by_status} dataKey="amount" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${fmtK(value)}`} labelLine={{ strokeWidth: 1 }} style={{ cursor: 'pointer' }} onClick={(e) => { const v = e?.payload?.status ?? e?.status ?? e?.name; openDrill('status', v, 'Status: ' + v); }}>
                        {data.by_status.map((s, i) => <Cell key={i} fill={STATUS_COLORS[s.status] || COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">MoM Growth %</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.monthly.filter(m => m.growth_pct !== undefined)} onClick={(s) => s?.activeLabel && openDrill('month', s.activeLabel, 'Month: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="_id" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="growth_pct" name="Growth %" fill="#0F4C81" radius={[4, 4, 0, 0]}>
                        {data.monthly.map((m, i) => <Cell key={i} fill={m.growth_pct >= 0 ? '#40916C' : '#DC2626'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* ─── Clinics Tab ─── */}
          {tab === 'clinics' && (
            <>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Clinic Revenue Ranking</h3>
                <ResponsiveContainer width="100%" height={Math.max(200, (data.clinics?.length || 1) * 40)}>
                  <BarChart data={data.clinics} layout="vertical" margin={{ left: 120 }} onClick={(s) => s?.activeLabel && openDrill('clinic', s.activeLabel, 'Clinic: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="clinic_name" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Revenue" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                    <th className="text-left p-2.5">Clinic</th><th className="text-right p-2.5">Net Revenue</th><th className="text-right p-2.5">Credit Notes</th><th className="text-right p-2.5">HA Revenue</th><th className="text-right p-2.5">Invoices</th>
                    <th className="text-right p-2.5">Customers</th><th className="text-right p-2.5">Avg Ticket</th><th className="text-right p-2.5">Outstanding</th><th className="text-right p-2.5">Collection %</th>
                  </tr></thead>
                  <tbody>
                    {(data.clinics || []).map((c, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                        <td className="p-2.5 font-medium"><button onClick={() => openDrill('clinic', c.clinic_name, 'Clinic: ' + c.clinic_name)} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 text-left" data-testid={`drill-clinic-${i}`}>{c.clinic_name}</button></td>
                        <td className="p-2.5 text-right font-semibold">{fmt(c.revenue)}</td>
                        <td className="p-2.5 text-right text-red-600">{fmt(c.credit_notes)}</td>
                        <td className="p-2.5 text-right text-emerald-700">{fmt(c.ha_revenue)}</td>
                        <td className="p-2.5 text-right">{c.count}</td>
                        <td className="p-2.5 text-right">{c.customer_count}</td>
                        <td className="p-2.5 text-right">{fmt(c.avg_ticket)}</td>
                        <td className="p-2.5 text-right text-[#DC2626]">{fmt(c.outstanding)}</td>
                        <td className="p-2.5 text-right"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.collection_rate >= 80 ? 'bg-green-50 text-green-700' : c.collection_rate >= 50 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>{c.collection_rate}%</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Clinic Monthly Trend */}
              {data.heatmap && Object.keys(data.heatmap).length > 0 && (
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Top 5 Clinic — Monthly Revenue Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={(() => {
                      const months = new Set();
                      Object.values(data.heatmap).forEach(m => Object.keys(m).forEach(k => months.add(k)));
                      const top5 = (data.clinics || []).slice(0, 5).map(c => c.clinic_name);
                      return Array.from(months).sort().map(month => {
                        const row = { month };
                        top5.forEach(name => { row[name] = data.heatmap[name]?.[month]?.revenue || 0; });
                        return row;
                      });
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {(data.clinics || []).slice(0, 5).map((c, i) => (
                        <Line key={c.clinic_name} type="monotone" dataKey={c.clinic_name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}

          {/* ─── Audiologists Tab ─── */}
          {tab === 'audiologists' && (
            <>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Audiologist Revenue Ranking</h3>
                <ResponsiveContainer width="100%" height={Math.max(200, (data.audiologists?.length || 1) * 45)}>
                  <BarChart data={data.audiologists} layout="vertical" margin={{ left: 140 }} onClick={(s) => s?.activeLabel && openDrill('audiologist', s.activeLabel, 'Audiologist: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Revenue" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                    <th className="text-left p-2.5">Audiologist</th><th className="text-right p-2.5">Revenue</th><th className="text-right p-2.5">HA Revenue</th><th className="text-right p-2.5">Invoices</th>
                    <th className="text-right p-2.5">Customers</th><th className="text-right p-2.5">SOs</th><th className="text-right p-2.5">Avg Ticket</th><th className="text-right p-2.5">Conv %</th><th className="text-right p-2.5">Coll %</th>
                  </tr></thead>
                  <tbody>
                    {(data.audiologists || []).map((a, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                        <td className="p-2.5 font-medium"><button onClick={() => openDrill('audiologist', a.name, 'Audiologist: ' + a.name)} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 text-left" data-testid={`drill-aud-${i}`}>{a.name}</button></td>
                        <td className="p-2.5 text-right font-semibold">{fmt(a.revenue)}</td>
                        <td className="p-2.5 text-right text-red-600">{fmt(a.credit_notes)}</td>
                        <td className="p-2.5 text-right text-emerald-700">{fmt(a.ha_revenue)}</td>
                        <td className="p-2.5 text-right">{a.invoice_count}</td>
                        <td className="p-2.5 text-right">{a.customer_count}</td>
                        <td className="p-2.5 text-right">{a.so_count}</td>
                        <td className="p-2.5 text-right">{fmt(a.avg_ticket)}</td>
                        <td className="p-2.5 text-right">{a.conversion_rate}%</td>
                        <td className="p-2.5 text-right"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${a.collection_rate >= 80 ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{a.collection_rate}%</span></td>
                      </tr>
                    ))}
                    {!data.audiologists.length && <tr><td colSpan={9} className="text-center py-6 text-[#64748B]">No audiologist data</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ─── Lead Sources Tab ─── */}
          {tab === 'lead_sources' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue by Lead Source</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={data.sources} dataKey="revenue" nameKey="source" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${fmtK(value)}`} style={{ cursor: 'pointer' }} onClick={(e) => { const v = e?.payload?.source ?? e?.source ?? e?.name; openDrill('lead_source', v, 'Lead Source: ' + v); }}>
                        {data.sources.map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Lead Source Breakdown</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.sources} onClick={(s) => s?.activeLabel && openDrill('lead_source', s.activeLabel, 'Lead Source: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" name="Revenue" fill="#0F4C81" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="avg_ticket" name="Avg Ticket" fill="#95D5B2" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                    <th className="text-left p-2.5">Lead Source</th><th className="text-right p-2.5">Net Revenue</th><th className="text-right p-2.5">Credit Notes</th><th className="text-right p-2.5">Invoices</th><th className="text-right p-2.5">Avg Ticket</th>
                  </tr></thead>
                  <tbody>
                    {data.sources.map((s, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2.5 font-medium"><button onClick={() => openDrill('lead_source', s.source, 'Lead Source: ' + s.source)} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 text-left" data-testid={`drill-source-${i}`}>{s.source}</button></td><td className="p-2.5 text-right font-semibold">{fmt(s.revenue)}</td><td className="p-2.5 text-right text-red-600">{fmt(s.credit_notes)}</td><td className="p-2.5 text-right">{s.count}</td><td className="p-2.5 text-right">{fmt(s.avg_ticket)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ─── Products Tab ─── */}
          {tab === 'products' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue by Category</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={data.by_category || []} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${fmtK(value)}`} style={{ cursor: 'pointer' }} onClick={(e) => { const v = e?.payload?.category ?? e?.category ?? e?.name; openDrill('category', v, 'Category: ' + v); }}>
                        {(data.by_category || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Units Sold by Category</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={(data.by_category || []).slice(0, 10)} onClick={(s) => s?.activeLabel && openDrill('category', s.activeLabel, 'Category: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="category" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" height={70} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="qty" name="Units" fill="#52B788" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Top Selling Products by Revenue</h3>
                <ResponsiveContainer width="100%" height={Math.max(200, (data.products?.length || 5) * 35)}>
                  <BarChart data={(data.products || []).slice(0, 15)} layout="vertical" margin={{ left: 180 }} onClick={(s) => s?.activeLabel && openDrill('product', s.activeLabel, 'Product: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={180} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Revenue" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                    <th className="text-left p-2.5">Product</th><th className="text-left p-2.5">Category</th><th className="text-right p-2.5">Revenue</th><th className="text-right p-2.5">Qty</th><th className="text-right p-2.5">Invoices</th><th className="text-right p-2.5">Avg Rate</th>
                  </tr></thead>
                  <tbody>
                    {(data.products || []).map((p, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2.5 font-medium"><button onClick={() => openDrill('product', p.name, 'Product: ' + p.name)} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 text-left" data-testid={`drill-product-${i}`}>{p.name}</button></td><td className="p-2.5 text-[#64748B]">{p.category ? <button onClick={() => openDrill('category', p.category, 'Category: ' + p.category)} className="text-[#0F4C81] underline decoration-dotted underline-offset-2">{p.category}</button> : '-'}</td><td className="p-2.5 text-right font-semibold">{fmt(p.revenue)}</td><td className="p-2.5 text-right">{p.quantity_sold}</td><td className="p-2.5 text-right">{p.invoice_count}</td><td className="p-2.5 text-right">{fmt(p.avg_rate)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ─── Collections Tab ─── */}
          {tab === 'collections' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Monthly Collections</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.monthly} onClick={(s) => s?.activeLabel && openDrill('payment_month', s.activeLabel, 'Collections: ' + s.activeLabel)} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="amount" name="Collected" fill="#40916C" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Payment Mode Mix</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={data.by_mode} dataKey="amount" nameKey="mode" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${fmtK(value)}`} style={{ cursor: 'pointer' }} onClick={(e) => { const v = e?.payload?.mode ?? e?.mode ?? e?.name; openDrill('payment_mode', v, 'Payment Mode: ' + v); }}>
                        {data.by_mode.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {/* Aging Report */}
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Outstanding Receivables Aging</h3>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {Object.entries(data.aging || {}).map(([bucket, amount]) => (
                    <div key={bucket} className={`rounded-lg p-3 text-center ${bucket === '90+' ? 'bg-red-50' : bucket === '61-90' ? 'bg-orange-50' : bucket === '31-60' ? 'bg-yellow-50' : 'bg-green-50'}`}>
                      <p className="text-[10px] text-[#64748B] uppercase">{bucket} Days</p>
                      <p className={`text-lg font-bold ${bucket === '90+' ? 'text-red-700' : bucket === '61-90' ? 'text-orange-700' : bucket === '31-60' ? 'text-yellow-700' : 'text-green-700'}`}>{fmt(amount)}</p>
                    </div>
                  ))}
                </div>
                {data.aging_details?.length > 0 && (
                  <table className="w-full text-xs">
                    <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-[10px] uppercase"><th className="text-left p-2">Invoice</th><th className="text-left p-2">Customer</th><th className="text-left p-2">Date</th><th className="text-right p-2">Days</th><th className="text-right p-2">Balance</th><th className="text-center p-2">Aging</th></tr></thead>
                    <tbody>
                      {data.aging_details.map((d, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-2 font-mono">{d.invoice_number}</td><td className="p-2">{d.customer_name}</td>
                          <td className="p-2">{d.date}</td><td className="p-2 text-right">{d.days}</td>
                          <td className="p-2 text-right font-medium text-[#DC2626]">{fmt(d.balance)}</td>
                          <td className="p-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded ${d.bucket === '90+' ? 'bg-red-100 text-red-700' : d.bucket === '61-90' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>{d.bucket}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* ─── Customers Tab ─── */}
          {tab === 'customers' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Top Customers by Revenue</h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, (data.top_customers?.length || 5) * 30)}>
                    <BarChart data={(data.top_customers || []).slice(0, 10)} layout="vertical" margin={{ left: 120 }} onClick={(s) => s?.activeLabel && setDrill({ name: s.activeLabel })} style={{ cursor: 'pointer' }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" name="Revenue" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue by State</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={data.state_wise} dataKey="revenue" nameKey="state" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${fmtK(value)}`} style={{ cursor: 'pointer' }} onClick={(e) => { const v = e?.payload?.state ?? e?.state ?? e?.name; openDrill('state', v, 'State: ' + v); }}>
                        {(data.state_wise || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                    <th className="text-left p-2.5">Customer</th><th className="text-right p-2.5">Revenue</th><th className="text-right p-2.5">Invoices</th><th className="text-right p-2.5">Outstanding</th>
                  </tr></thead>
                  <tbody>
                    {(data.top_customers || []).map((c, i) => (
                      <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]"><td className="p-2.5 font-medium"><button onClick={() => setDrill({ name: c.name })} className="text-[#0F4C81] underline decoration-dotted underline-offset-2 hover:text-[#0F4C81] text-left" data-testid={`analytics-customer-${i}`}>{c.name}</button></td><td className="p-2.5 text-right font-semibold">{fmt(c.revenue)}</td><td className="p-2.5 text-right text-red-600">{fmt(c.credit_notes)}</td><td className="p-2.5 text-right">{c.count}</td><td className="p-2.5 text-right text-[#DC2626]">{fmt(c.outstanding)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
      <CustomerDrillDownDialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }} customerName={drill?.name || ''} customerId={drill?.id || ''} />
      <AnalyticsDrillDialog spec={drillSpec} startDate={startDate} endDate={endDate} onClose={() => setDrillSpec(null)} />
    </div>
  );
}
